import { DocumentData } from '@angular/fire/firestore';
import { WorkspaceOnboarding, WorkspaceRole, WorkspaceType, ownerLikeWorkspaceType } from '@shared/definitions/workspace';
import { Workspace } from '@core/services/auth/workspace.service.types';

/**
 * Detect a freshly signed-up user — creationTime within ~60s of lastSignInTime
 * means the workspace creation Cloud Function may still be running and we
 * should give it the longer 3×2s retry budget. Returning users get the tight
 * 1×1s budget instead (the slow path was costing them up to 6s on every login).
 *
 * Exported so unit tests can validate the decision deterministically without
 * needing to spy on @angular/fire's frozen ESM exports.
 */
export function isFreshSignupFromMetadata(
  metadata: { creationTime?: string; lastSignInTime?: string } | null | undefined
): boolean {
  if (!metadata) return false;
  const creation = metadata.creationTime ? new Date(metadata.creationTime).getTime() : 0;
  const lastSignIn = metadata.lastSignInTime ? new Date(metadata.lastSignInTime).getTime() : 0;
  return creation > 0 && lastSignIn > 0 && (lastSignIn - creation) < 60_000;
}

/**
 * Build a full Workspace object from Firestore document data (used for the
 * one-time read + real-time listener of the active workspace).
 */
export function buildWorkspaceFromData(id: string, data: DocumentData): Workspace {
  return {
    id: id,
    workspaceId: id, // Add for compatibility
    name: data['name'],
    type: data['type'] || 'personal',
    color: data['color'], // Workspace color for UI display
    icon: data['icon'], // Workspace icon
    // Professional contact for the tenant lockout screen (#494).
    contactName: data['contactName'] || undefined,
    contactEmail: data['contactEmail'] || undefined,
    contactPhone: data['contactPhone'] || undefined,
    ownerId: data['ownerId'],
    ownerEmail: data['ownerEmail'] || '',
    members: data['members'],
    createdAt: data['createdAt']?.toDate?.() || new Date(data['createdAt']),
    updatedAt: data['updatedAt']?.toDate?.() || new Date(data['updatedAt']),
    // Include stats for billing and usage tracking
    stats: data['stats'] || null,
    // Include subscription details for billing page
    subscription: data['subscription'] ? {
      ...data['subscription'],
      currentPeriodStart: data['subscription'].currentPeriodStart?.toDate?.() ||
        (data['subscription'].currentPeriodStart ? new Date(data['subscription'].currentPeriodStart) : undefined),
      currentPeriodEnd: data['subscription'].currentPeriodEnd?.toDate?.() ||
        (data['subscription'].currentPeriodEnd ? new Date(data['subscription'].currentPeriodEnd) : undefined),
    } : undefined,
    // Include billing details (now includes cached payment method info)
    billing: data['billing'] || undefined,
    // Complimentary ("comp") grant — superadmin-authored, function-only field.
    // MUST be mapped through so every UI billing gate (add-property paywall,
    // grace prompt, paid-feature locks) can consult isWorkspaceCompActive().
    // Pass the raw value: isWorkspaceCompActive coerces Firestore Timestamps.
    comp: data['comp'] || undefined,
    // Cockpit inbox (slug + archive token) — populated by setCockpitInboundSlug
    inbound: data['inbound'] || undefined,
    // Cockpit behaviour switches (auto-acknowledge #259, key-date reminder
    // tiers, vacancy threshold). This builder is an explicit ALLOW-LIST: a
    // field absent from it is invisible to the whole frontend, however well the
    // write landed in Firestore — which is exactly how the auto-acknowledge
    // toggle appeared to save and then reverted on reload.
    cockpitSettings: data['cockpitSettings'] || undefined,
    // Post-signup onboarding state (owner workspaces; server-authored only)
    onboarding: mapOnboardingFromData(data),
    getUserRole: (userId: string): WorkspaceRole | null => {
      const member = data['members']?.[userId];
      return (member?.role as WorkspaceRole) || null;
    }
  };
}

/** Firestore `onboarding` block → typed shape with Dates (undefined when absent). */
function mapOnboardingFromData(data: DocumentData): WorkspaceOnboarding | undefined {
  const raw = data['onboarding'];
  if (!raw) return undefined;
  return {
    ...raw,
    startedAt: raw.startedAt?.toDate?.() || (raw.startedAt ? new Date(raw.startedAt) : new Date()),
    completedAt: raw.completedAt?.toDate?.() || (raw.completedAt ? new Date(raw.completedAt) : undefined),
    skippedAt: raw.skippedAt?.toDate?.() || (raw.skippedAt ? new Date(raw.skippedAt) : undefined),
  };
}

/**
 * Build the lighter Workspace list-entry shape used by `loadUserWorkspaces`
 * (no comp / inbound — intentionally kept distinct from `buildWorkspaceFromData`
 * to preserve the exact previous behavior). `onboarding` IS carried: the login
 * auto-selection needs it to avoid restoring a workspace whose wizard was
 * abandoned (see `pickAutoSelectWorkspace`).
 */
export function buildWorkspaceListEntryFromData(id: string, data: DocumentData): Workspace {
  return {
    id: id,
    workspaceId: id, // Add for compatibility
    name: data['name'],
    type: data['type'] || 'personal',
    color: data['color'], // Workspace color for UI display
    icon: data['icon'], // Workspace icon
    ownerId: data['ownerId'],
    ownerEmail: data['ownerEmail'] || '',
    members: data['members'],
    createdAt: data['createdAt']?.toDate?.() || new Date(data['createdAt']),
    updatedAt: data['updatedAt']?.toDate?.() || new Date(data['updatedAt']),
    onboarding: mapOnboardingFromData(data),
    // Include stats for billing and usage tracking
    stats: data['stats'] || null,
    // Include subscription details for billing page
    subscription: data['subscription'] ? {
      ...data['subscription'],
      currentPeriodStart: data['subscription'].currentPeriodStart?.toDate?.() ||
        (data['subscription'].currentPeriodStart ? new Date(data['subscription'].currentPeriodStart) : undefined),
      currentPeriodEnd: data['subscription'].currentPeriodEnd?.toDate?.() ||
        (data['subscription'].currentPeriodEnd ? new Date(data['subscription'].currentPeriodEnd) : undefined),
    } : undefined,
    // Include billing details
    billing: data['billing'] || undefined,
    getUserRole: (userId: string): WorkspaceRole | null => {
      const member = data['members']?.[userId];
      return (member?.role as WorkspaceRole) || null;
    }
  };
}

/**
 * Same predicate as `OnboardingCompletedGuard` (onboarding.guard.ts): an
 * owner-like workspace whose `onboarding.completed` is not `true` is sent to
 * `/onboarding` the moment it becomes the active workspace. A missing block
 * counts as unfinished, exactly like the guard reads it.
 */
export function isStuckInOnboarding(w: Pick<Workspace, 'type' | 'onboarding'>): boolean {
  return isOwnerLike(w) && !w.onboarding?.completed;
}

/**
 * `Workspace.type` is typed `string | undefined` on the service side; the list
 * builders always default it to `'personal'`, so a missing type is treated as
 * an owner-like personal workspace here too.
 */
function isOwnerLike(w: Pick<Workspace, 'type'>): boolean {
  return ownerLikeWorkspaceType((w.type ?? 'personal') as WorkspaceType);
}

/**
 * A `personal` workspace the account never really used, on an account that is
 * a tenant somewhere (#298, #619).
 *
 * Before the invitation race was closed, an invited tenant could be handed an
 * auto-created `personal_{uid}` owner workspace. Nothing marks it as automatic
 * — both creators write the same document — so it is recognised by what never
 * happened to it: its owner wizard was never finished and it holds no
 * property. A real owner who is also a tenant finished their wizard or added a
 * property; a brand-new owner has no tenant workspace. Neither is caught.
 */
export function isDormantAutoPersonalWorkspace(
  w: Pick<Workspace, 'type' | 'ownerId' | 'onboarding' | 'stats'>,
  all: readonly Pick<Workspace, 'type'>[],
  userId: string,
): boolean {
  if (w.type !== 'personal' || w.ownerId !== userId) return false;
  if (!all.some(other => other.type === 'tenant')) return false;
  if (w.onboarding?.completed) return false;
  return !((w.stats?.propertyCount ?? 0) > 0);
}

/** The spaces worth offering in a switcher: all of them but the ghost above. */
export function switchableWorkspaces<T extends Workspace>(all: readonly T[], userId: string): T[] {
  return all.filter(w => !isDormantAutoPersonalWorkspace(w, all, userId));
}

export type AutoSelectReason = 'last-active' | 'personal' | 'single' | 'finished-fallback';

/**
 * Which workspace to activate at a cold login, when no tab-level selection
 * survives (`localStorage.currentWorkspaceId` is wiped on sign-out).
 *
 * Order:
 *  1. the stored `lastActiveWorkspaceId`, as long as it doesn't lead straight
 *     into an unfinished wizard while a finished workspace exists — that is
 *     the prod case of 2026-09-14: the preference was written when a
 *     professional workspace was created, its wizard abandoned, and every
 *     fresh sign-in since landed on step 4/5 instead of the private space;
 *  2. the user's own personal workspace;
 *  3. any finished owner-like workspace (a personal one may itself be stuck);
 *  4. the preference anyway when EVERYTHING is unfinished (brand-new user —
 *     the wizard is where they belong);
 *  5. the only workspace, when there is one;
 *  6. `null` — several workspaces, no clear default: the user picks.
 *
 * Pure so the matrix is unit-testable; the caller does the `setCurrentWorkspace`
 * and must NOT persist the preference (auto-selections never overwrite it).
 */
export function pickAutoSelectWorkspace(
  workspaces: readonly Workspace[],
  lastActiveWorkspaceId: string | null,
  userId: string,
): { id: string; reason: AutoSelectReason } | null {
  if (workspaces.length === 0) return null;

  const last = lastActiveWorkspaceId ?
    workspaces.find(w => w.id === lastActiveWorkspaceId) :
    undefined;
  if (last && !isStuckInOnboarding(last)) {
    return { id: last.id, reason: 'last-active' };
  }

  // A ghost personal workspace (#298) is never "home": a tenant must land in
  // their tenant space, not in an owner wizard they never asked for.
  const ownPersonal = workspaces.find(w =>
    w.type === 'personal' && w.ownerId === userId &&
    !isDormantAutoPersonalWorkspace(w, workspaces, userId),
  );

  if (last) {
    // Preference leads into an abandoned wizard: prefer somewhere finished.
    if (ownPersonal && !isStuckInOnboarding(ownPersonal)) {
      return { id: ownPersonal.id, reason: 'personal' };
    }
    // Owner-like only: a tenant/provider membership is not "home" for an owner.
    const finished = workspaces.find(w =>
      w.id !== last.id && isOwnerLike(w) && !isStuckInOnboarding(w),
    );
    if (finished) {
      return { id: finished.id, reason: 'finished-fallback' };
    }
    return { id: last.id, reason: 'last-active' };
  }

  if (ownPersonal) {
    return { id: ownPersonal.id, reason: 'personal' };
  }
  if (workspaces.length === 1) {
    return { id: workspaces[0].id, reason: 'single' };
  }
  return null;
}

/**
 * Whether the "no workspace found → create a personal one" safety net must
 * stand down for this account.
 *
 * The tenant and service-provider invitation signups set
 * `skipPersonalWorkspace` on `users/{uid}`, and `onUserCreatedWorkspace`
 * honours it by creating nothing. For a short window the account therefore has
 * ZERO workspaces — which is the normal state, not a failed trigger. The
 * dedicated space arrives a moment later from `createTenantWorkspace` /
 * `createServiceProviderWorkspace`.
 *
 * Without this check the fallback read that silence as a breakdown and called
 * `createPersonalWorkspace`, recreating the exact workspace the flag asked to
 * avoid: the trigger logged "Skipping personal workspace" while an orphan
 * `personal_{uid}` appeared anyway (#300).
 *
 * Takes the raw `users/{uid}` payload (or null when unreadable) so the
 * decision stays testable — the Firestore read itself lives in the service.
 * Returns false for a missing/unreadable document: recreating a workspace is
 * recoverable, leaving a real owner with none is not.
 */
export function skipsPersonalWorkspaceFallback(
  userDocData: DocumentData | null | undefined,
): boolean {
  return userDocData?.['skipPersonalWorkspace'] === true;
}

/**
 * Whether a workspace document has been soft-deleted.
 *
 * `deleteWorkspace` (backend/functions/src/workspaceOperations.ts) does NOT
 * remove the document: it stamps `deletedAt` and a scheduled job purges it 30
 * days later, so the owner can change their mind. Nothing on the read path
 * ever looked at that field, so a deleted workspace stayed in the switcher,
 * stayed selectable, and kept accepting data until the purge — the user saw
 * their deletion apparently fail, and could go on filling a workspace that
 * was scheduled to disappear.
 *
 * Read from the RAW document data rather than from the built entry:
 * `buildWorkspaceListEntryFromData` does not carry `deletedAt`, and widening
 * the entry would push the field into every screen that renders one.
 */
export function isSoftDeletedWorkspace(data: DocumentData | null | undefined): boolean {
  return !!data?.['deletedAt'];
}
